/**
 * The intake and files seams, in memory (MASTER_PLAN M8.7).
 *
 * What it is for, in order of how often it is used:
 *
 *  1. **Tests.** Every paperwork, insurance-card and portal-intake test injects one of
 *     these, so a screen is driven exactly as a patient drives it with no server and no
 *     fetch (TESTING.md §2).
 *  2. **`VITE_USE_MOCKS=true`.** The funnel and the portal walk end to end with no API.
 *
 * It is a fixture, not a second implementation of the rules: the merge, the conditional
 * drop and the validation all go through the **same** `validateAnswers` from
 * `@platform/contracts` the API runs (INTAKE_FORMS.md §8A.1), so a form that behaves here
 * behaves there. What it fakes is storage, a scanner and a clock.
 *
 * No PHI: the definition is the contract's own `sampleIntakeV1` and every seeded answer is
 * invented (rules §25).
 */

import { ApiError } from '@platform/api-client';
import {
  sampleIntakeV1,
  validateAnswers,
  type AnswerSet,
  type DownloadTicket,
  type FileSummary,
  type IntakePacket,
  type PublishedForm,
  type UploadTicket,
} from '@platform/contracts';

import type { FilesClient, PatientFileKind, UploadOptions } from './files';
import { definedAnswers, type IntakeClient } from './intake';

export interface MockIntakeOptions {
  /** The definition to serve. Defaults to the contract's sample. */
  form?: PublishedForm;
  /** Answers the submission starts with — a patient who has been here before. */
  answers?: AnswerSet;
  /** Every call rejects, for the "we could not reach the clinic" state. */
  failing?: boolean;
  /** Injected clock (TESTING.md §2.1). */
  now?: () => Date;
}

const SAMPLE_FORM: PublishedForm = {
  formId: '00000000-0000-4000-8000-00000000f001',
  versionId: '00000000-0000-4000-8000-00000000f002',
  version: sampleIntakeV1.version,
  key: sampleIntakeV1.key,
  title: sampleIntakeV1.title,
  definition: sampleIntakeV1,
};

const SUBMISSION_ID = '00000000-0000-4000-8000-00000000f003';
const DRAFT_ID = '00000000-0000-4000-8000-00000000f004';

/**
 * A form somebody has started and not finished: the privacy acknowledgement and the
 * identity block are done, the allergy history is not.
 *
 * Invented, and obviously so — no name, address, telephone number or date of birth here
 * belongs to a person (rules §25.1). It lived in `portal.mock.ts` until M8.7 moved intake
 * out of the portal seam; both the portal's tests and the funnel's read it from here.
 */
export const SAMPLE_PARTIAL_ANSWERS: AnswerSet = {
  nppAck: true,
  firstName: 'Sam',
  lastName: 'Example',
  dateOfBirth: '1990-04-12',
  sexAtBirth: 'undisclosed',
  isMinor: false,
  email: 'sam@example.test',
  phone: '(555) 010-4477',
  state: 'NY',
  postalCode: '10001',
  preferredContact: 'email',
};

/** The same form, finished — enough for `validateAnswers` to call it complete. */
export const SAMPLE_SUBMITTED_ANSWERS: AnswerSet = {
  ...SAMPLE_PARTIAL_ANSWERS,
  allergyTypes: ['environmental'],
  reactionCount: 2,
  carriesEpinephrine: false,
  medications: 'None',
  hasInsurance: true,
  payerName: 'Example Health',
  memberId: 'EX0000000',
  telehealthConsent: true,
  signature: { name: 'Sam Example', consentedAt: '2026-08-04T15:12:00.000Z' },
};

export interface MockIntakeClient extends IntakeClient {
  /** What the fixture holds right now, for a test that wants to assert storage. */
  readonly packet: () => IntakePacket | null;
}

export function createMockIntakeClient(options: MockIntakeOptions = {}): MockIntakeClient {
  const form = options.form ?? SAMPLE_FORM;
  const now = options.now ?? (() => new Date());

  /** One submission per anchor. `null` is the funnel's "nothing started yet". */
  let funnel: IntakePacket | null = null;
  const byAppointment = new Map<string, IntakePacket>();

  function refuse(): never {
    throw new Error('We could not reach the clinic. Please try again.');
  }

  /**
   * Merge, clean, validate — the API's own order (INTAKE_FORMS.md §8A.2).
   *
   * `values` is what is stored: hidden answers dropped, unknown ids dropped, strings
   * trimmed. Handing back the *cleaned* set is what lets a renderer that posted one field
   * learn which other answers the save dropped.
   */
  function apply(packet: IntakePacket, patch: AnswerSet, submit: boolean): IntakePacket {
    const merged = { ...packet.answers, ...patch };
    const partial = validateAnswers(form.definition, merged, { partial: true });
    const full = validateAnswers(form.definition, partial.values);

    if (submit && !full.valid) {
      /* The API's own refusal, envelope and all, so the screen's error path is exercised
         rather than a fixture-shaped approximation of it (API.md §1.6). */
      throw new ApiError({
        status: 422,
        code: 'INTAKE_INCOMPLETE',
        message: 'Some answers are still needed.',
        correlationId: null,
        details: { errors: full.errors },
      });
    }

    return {
      ...packet,
      answers: definedAnswers(partial.values),
      updatedAt: now().toISOString(),
      ...(submit
        ? {
            submittedAt: now().toISOString(),
            status:
              packet.appointmentId === null ? ('in_progress' as const) : ('submitted' as const),
          }
        : {}),
      validation: { valid: full.valid, errors: full.errors },
    };
  }

  function seed(anchor: { appointmentId: string | null; draftId: string | null }): IntakePacket {
    const answers = options.answers ?? {};
    const full = validateAnswers(form.definition, answers);
    return {
      submissionId: SUBMISSION_ID,
      appointmentId: anchor.appointmentId,
      draftId: anchor.draftId,
      status: 'assigned',
      submittedAt: null,
      updatedAt: now().toISOString(),
      formId: form.formId,
      formVersionId: form.versionId,
      formTitle: form.title,
      formVersion: form.version,
      definition: form.definition,
      answers: definedAnswers(answers),
      validation: { valid: full.valid, errors: full.errors },
    };
  }

  return {
    packet: () => funnel,

    publishedForm: () => (options.failing === true ? refuse() : Promise.resolve(form)),

    currentFunnelPacket: () => (options.failing === true ? refuse() : Promise.resolve(funnel)),

    startFunnelPacket: () => {
      if (options.failing === true) refuse();
      funnel ??= seed({ appointmentId: null, draftId: DRAFT_ID });
      return Promise.resolve(funnel);
    },

    saveFunnelAnswers: (answers) => {
      if (options.failing === true) refuse();
      funnel ??= seed({ appointmentId: null, draftId: DRAFT_ID });
      funnel = apply({ ...funnel, status: 'in_progress' }, answers, false);
      return Promise.resolve(funnel);
    },

    completeFunnelIntake: (answers = {}) => {
      if (options.failing === true) refuse();
      funnel ??= seed({ appointmentId: null, draftId: DRAFT_ID });
      funnel = apply(funnel, answers, true);
      return Promise.resolve(funnel);
    },

    appointmentPacket: (appointmentId) => {
      if (options.failing === true) refuse();
      const existing = byAppointment.get(appointmentId);
      if (existing !== undefined) return Promise.resolve(existing);
      const created = seed({ appointmentId, draftId: null });
      byAppointment.set(appointmentId, created);
      return Promise.resolve(created);
    },

    saveAppointmentAnswers: (appointmentId, answers, submit) => {
      if (options.failing === true) refuse();
      const existing = byAppointment.get(appointmentId) ?? seed({ appointmentId, draftId: null });
      const next = apply({ ...existing, status: 'in_progress' }, answers, submit);
      byAppointment.set(appointmentId, next);
      return Promise.resolve(next);
    },
  };
}

/* ------------------------------------------------------------------ *
 * Files
 * ------------------------------------------------------------------ */

export interface MockFilesOptions {
  /** Hold every upload at `scanning`, so the "still being checked" state has a test. */
  scanPending?: boolean;
  /** Every call rejects. */
  failing?: boolean;
  now?: () => Date;
}

/**
 * An in-memory object store.
 *
 * The bytes are kept as a `Blob` and handed back as an object URL when the environment
 * has one (a browser) and as a stable placeholder when it does not (jsdom), so a
 * thumbnail has a `src` to assert without a network or a filesystem.
 */
export function createMockFilesClient(options: MockFilesOptions = {}): FilesClient {
  const now = options.now ?? (() => new Date());
  const files = new Map<string, { summary: FileSummary; blob: Blob }>();
  let counter = 0;

  function refuse(): never {
    throw new Error('We could not upload that file. Please try again.');
  }

  function nextId(): string {
    counter += 1;
    return `00000000-0000-4000-8000-${String(counter).padStart(12, '0')}`;
  }

  return {
    requestUpload: (input) => {
      if (options.failing === true) refuse();
      const fileId = nextId();
      files.set(fileId, {
        blob: new Blob([]),
        summary: {
          id: fileId,
          kind: input.kind,
          ownerKind: 'draft',
          contentType: input.contentType,
          sizeBytes: input.sizeBytes,
          status: 'pending',
          scanStatus: 'pending',
          downloadable: false,
          name: input.fileName ?? null,
          sha256: null,
          createdAt: now().toISOString(),
        },
      });
      return Promise.resolve<UploadTicket>({
        fileId,
        uploadUrl: `memory://${fileId}`,
        method: 'PUT',
        headers: { 'content-type': input.contentType },
        maxSizeBytes: 10 * 1024 * 1024,
        expiresAt: new Date(now().getTime() + 5 * 60_000).toISOString(),
      });
    },

    putBytes: (ticket, file, uploadOptions: UploadOptions = {}) => {
      if (options.failing === true) refuse();
      const entry = files.get(ticket.fileId);
      if (entry !== undefined) entry.blob = file;
      uploadOptions.onProgress?.({ loaded: file.size, total: file.size, ratio: 1 });
      return Promise.resolve();
    },

    complete: (fileId) => {
      if (options.failing === true) refuse();
      const entry = files.get(fileId);
      if (entry === undefined) throw new Error('We could not find that upload.');
      const scanning = options.scanPending === true;
      entry.summary = {
        ...entry.summary,
        status: 'available',
        scanStatus: scanning ? 'pending' : 'clean',
        downloadable: !scanning,
      };
      return Promise.resolve(entry.summary);
    },

    downloadUrl: (fileId) => {
      if (options.failing === true) refuse();
      const entry = files.get(fileId);
      if (entry === undefined) throw new Error('We could not find that file.');
      if (!entry.summary.downloadable) {
        throw new ApiError({
          status: 409,
          code: 'CONFLICT',
          message: 'That file is still being checked. Please try again shortly.',
          correlationId: null,
        });
      }
      return Promise.resolve<DownloadTicket>({
        url: objectUrlFor(entry.blob, fileId),
        expiresAt: new Date(now().getTime() + 5 * 60_000).toISOString(),
        contentType: entry.summary.contentType,
        sizeBytes: entry.summary.sizeBytes,
      });
    },

    list: () => Promise.resolve([...files.values()].map((entry) => entry.summary)),

    remove: (fileId) => {
      files.delete(fileId);
      return Promise.resolve();
    },
  };
}

/** A URL a browser can render, or a stable stand-in where `createObjectURL` is absent. */
function objectUrlFor(blob: Blob, fileId: string): string {
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(blob);
  }
  return `memory://${fileId}`;
}

/** A funnel-shaped kind list, so a test can name one without importing the contract. */
export const MOCK_CARD_KINDS: PatientFileKind[] = ['insurance_card_front', 'insurance_card_back'];
